import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, FloatButton, Popconfirm, Tooltip } from 'antd';
import { CloseOutlined, CodeSandboxOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelectCargoStore } from 'shared/stores/use-select-cargos.ts';
import { CargoSelectAddToTrip } from 'components/cargos/cargos-select-info-widget/components/cargo-select-add-to-trip';
import { CargoSelectCreateTrip } from 'components/cargos/cargos-select-info-widget/components/cargo-select-create-trip';
import { useGetSelectedTotalValues } from '../../../api/calculate/use-get-selected-total-values.ts';
import styles from './cargos-select-info-widget.module.scss';

export const CargosSelectInfoWidget: React.FC = () => {
  const { selectedCargos, selectedCargoItems, deleteAll } = useSelectCargoStore();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const { data: totalValues, refetch } = useGetSelectedTotalValues({
    cargoIds: Array.from(selectedCargos.keys()),
    cargoItemIds: Array.from(selectedCargoItems.keys()),
    isEnabled: isOpen,
  });
  const toggle = () => setIsOpen((value) => !value);

  const [isOpenCreateForm, setIsOpenCreateFrom] = useState(false);
  const [isOpenAddForm, setIsOpenAddFrom] = useState(false);
  const handleOpenCreateForm = () => setIsOpenCreateFrom(true);
  const handleOpenAddForm = () => setIsOpenAddFrom(true);

  const stableCargoKeys = Array.from(selectedCargos.keys()).join(',');
  const stableCargoItemKeys = Array.from(selectedCargoItems.keys()).join(',');

  useEffect(() => {
    if (isOpen) {
      void refetch();
    }
  }, [stableCargoKeys, stableCargoItemKeys, refetch]);

  if (selectedCargos.size === 0 && selectedCargoItems.size === 0) return null;

  if (!isOpen) {
    return (
      <FloatButton
        tooltip='Selected cargos'
        type='primary'
        style={{ right: 74, bottom: 40 }}
        icon={<CodeSandboxOutlined />}
        onClick={toggle}
      />
    );
  }

  return (
    <div className={styles.container}>
      <CloseOutlined className={styles.close} onClick={toggle} />
      {totalValues ? (
        <>
          <div className={styles.title}>
            {t('selected:selected-cargos')}:{' '}
            <Tooltip placement='top' title={t('selected:number-of-cargo')}>
              {selectedCargos.size}
            </Tooltip>
            {' / '}
            <Tooltip placement='top' title={t('selected:number-of-separately-items')}>
              {totalValues?.separatelyCargoItems}
            </Tooltip>
            {' / '}
            <Tooltip placement='top' title={t('selected:number-of-selected-items')}>
              {selectedCargoItems.size}
            </Tooltip>
          </div>
          {totalValues.cargosWithoutWeight || totalValues.cargoItemsWithoutWeight ? (
            <div className={styles.warnInfo}>
              Without weight: {totalValues.cargosWithoutWeight} / {totalValues.cargoItemsWithoutWeight}
            </div>
          ) : null}
          <Flex gap='small' className={styles.info} vertical>
            <span>
              {t('common:LDM')}: {totalValues.totalLdm}
            </span>
            <span>
              {t('common:weight')}: {totalValues.totalWeight}
            </span>
            <span>
              {t('common:volume')}: {totalValues.totalVolume}
            </span>
          </Flex>
        </>
      ) : null}
      <Flex gap='small'>
        <Button key='submit' type='primary' onClick={handleOpenCreateForm}>
          {t('selected:create_new_trip')}
        </Button>
        <Button key='submit' type='primary' onClick={handleOpenAddForm}>
          {t('selected:add_to_trip')}
        </Button>
        <Popconfirm title={t('selected:clear-selected')} okText='Yes' cancelText='No' onConfirm={deleteAll}>
          <Button size='middle' icon={<DeleteOutlined />} type='primary' ghost />
        </Popconfirm>
      </Flex>
      <CargoSelectCreateTrip open={isOpenCreateForm} setOpen={setIsOpenCreateFrom} />
      <CargoSelectAddToTrip open={isOpenAddForm} setOpen={setIsOpenAddFrom} />
    </div>
  );
};
